import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Coachmark.css';

const LS_PREFIX = 'activities_hint_';

/**
 * Coachmark — one‑time onboarding tooltip anchored to a DOM element.
 *
 * Props:
 *   hintKey   – localStorage suffix (e.g. "row" → "activities_hint_row")
 *   selector  – CSS selector for the anchor element
 *   text      – tooltip body text
 *   placement – "bottom" | "right" (default "bottom")
 *   delay     – ms before showing (default 800)
 */
function Coachmark({ hintKey, selector, text, placement = 'bottom', delay = 800 }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const dismissed = useRef(false);
  const lsKey = LS_PREFIX + hintKey;

  // Check if already dismissed
  useEffect(() => {
    if (localStorage.getItem(lsKey)) {
      dismissed.current = true;
    }
  }, [lsKey]);

  // Position the coachmark relative to anchor
  const position = useCallback(() => {
    if (dismissed.current) return;
    const anchor = document.querySelector(selector);
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    if (placement === 'right') {
      setPos({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
        transformOrigin: 'left center',
        arrowClass: 'coachmark-arrow--left',
      });
    } else {
      setPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
        transformOrigin: 'top center',
        arrowClass: 'coachmark-arrow--top',
      });
    }
  }, [selector, placement]);

  // Show after delay, reposition on scroll/resize
  useEffect(() => {
    if (dismissed.current) return;

    const timer = setTimeout(() => {
      position();
      setVisible(true);
    }, delay);

    const reposition = () => { if (!dismissed.current) position(); };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [delay, position]);

  // Dismiss handler
  const dismiss = useCallback(() => {
    dismissed.current = true;
    localStorage.setItem(lsKey, '1');
    setVisible(false);
  }, [lsKey]);

  // Auto-dismiss when user interacts with the target
  useEffect(() => {
    if (dismissed.current) return;
    const anchor = document.querySelector(selector);
    if (!anchor) return;

    const handler = () => dismiss();
    anchor.addEventListener('click', handler);
    return () => anchor.removeEventListener('click', handler);
  }, [selector, dismiss]);

  if (!visible || !pos || dismissed.current) return null;

  return (
    <div
      className="coachmark"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className={`coachmark-arrow ${pos.arrowClass}`} />
      <div className="coachmark-body">
        <span className="coachmark-text">{text}</span>
        <button className="coachmark-dismiss" onClick={dismiss}>Got it</button>
      </div>
    </div>
  );
}

export default React.memo(Coachmark);
